"use client";

import { useMe } from "@/hooks/me/useMe";
import { useSession } from "next-auth/react";
export const usePermission = () => {
  const { user: meUser, loading: loadingMe } = useMe();
  const { data: session, status } = useSession();
  // Helper para obter os dados do usuário
  const getUserData = () => {
    if (!meUser) return null;
    return meUser?.user || meUser;
  };

  // Verifica se o usuário é um super admin (administra todos os estabelecimentos)
  const isSuperAdmin = (): boolean => {
    if (!session?.user) {
      return false;
    }

    const userData = getUserData();
    if (!userData) return false;

    // Verifica se tem role de super_admin
    const roles = userData?.roles || [];
    const hasSuperAdminRole = roles.some(
      (role: any) =>
        role?.slug === "super_admin" || role?.name === "super_admin",
    );

    // Verifica se tem permissão de super_admin
    const permissions = userData?.permissions || [];
    const hasSuperAdminPermission = permissions.includes("super_admin");

    // Verifica flag is_super_admin - aceita tanto boolean quanto numeric (1/0)
    const isSuperAdminValue = userData?.is_super_admin;
    const hasSuperAdminFlag =
      isSuperAdminValue === true ||
      isSuperAdminValue === 1 ||
      isSuperAdminValue === "1";

    // Verifica access object
    const access = userData?.access || meUser.user?.access;
    const accessValue = access?.is_super_admin;
    const hasAccessSuperAdmin =
      accessValue === true || accessValue === 1 || accessValue === "1";

    return (
      hasSuperAdminRole ||
      hasSuperAdminPermission ||
      hasSuperAdminFlag ||
      hasAccessSuperAdmin
    );
  };

  // Verifica se o usuário é owner de um estabelecimento específico
  const isOwner = (): boolean => {
    const user = getUserData();
    return user?.is_owner === true;
  };

  // Verifica permissões gerais (owner ou super admin sempre têm acesso)
  const hasPermission = (name: string): boolean => {
    const user = getUserData();
    if (!user?.permissions) return false;

    // Super admin tem acesso a tudo
    if (isSuperAdmin()) {
      return true;
    }

    // Owner do estabelecimento tem acesso a tudo do seu estabelecimento
    if (isOwner()) {
      return true;
    }

    return user.permissions.includes(name);
  };

  // Verifica se tem pelo menos uma das permissões listadas
  const hasAnyPermission = (names: string[]): boolean => {
    // Super admin tem acesso a tudo
    if (isSuperAdmin()) {
      return true;
    }

    // Owner do estabelecimento tem acesso a tudo do seu estabelecimento
    if (isOwner()) {
      return true;
    }

    return names.some((name) => hasPermission(name));
  };

  // Permissões específicas de super admin para gerenciar estabelecimentos
  const canManageEstablishments = (): boolean => {
    return isSuperAdmin() || hasPermission("manage_establishments");
  };

  // Pode ativar/desativar estabelecimentos
  const canToggleEstablishments = (): boolean => {
    return isSuperAdmin() || hasPermission("toggle_establishments");
  };

  // Pode configurar tokens e configurações globais
  const canManageGlobalSettings = (): boolean => {
    return isSuperAdmin() || hasPermission("manage_global_settings");
  };

  // Pode ver todos os estabelecimentos (dashboard global)
  const canViewAllEstablishments = (): boolean => {
    return isSuperAdmin() || hasPermission("view_all_establishments");
  };

  // Pode gerenciar usuários de qualquer estabelecimento
  const canManageAllUsers = (): boolean => {
    return isSuperAdmin() || hasPermission("manage_all_users");
  };

  // Pode ver relatórios de todos os estabelecimentos
  const canViewGlobalReports = (): boolean => {
    return isSuperAdmin() || hasPermission("view_global_reports");
  };

  // Verifica se pode gerenciar um estabelecimento específico
  const canManageEstablishment = (establishmentId?: number): boolean => {
    // Super admin pode gerenciar qualquer estabelecimento
    if (isSuperAdmin()) {
      return true;
    }

    // Owner só pode gerenciar seu próprio estabelecimento
    if (isOwner()) {
      const user = getUserData();
      if (!establishmentId) return true; // Se não especificou, assume que é o próprio
      return user?.merchantid === establishmentId;
    }

    return hasPermission("manage_establishment");
  };

  return {
    // Funções de verificação de papel
    isSuperAdmin,
    isOwner,

    // Funções de permissão gerais
    hasPermission,
    hasAnyPermission,

    // Funções específicas para super admin
    canManageEstablishments,
    canToggleEstablishments,
    canManageGlobalSettings,
    canViewAllEstablishments,
    canManageAllUsers,
    canViewGlobalReports,
    canManageEstablishment,

    // Dados da sessão (compatibilidade)
    loading: loadingMe,
    session: meUser ? { user: meUser } : null,
  };
};
